<template>
  <ResizablePanels :show-right="showingPreview">
    <template #panel-1>
      <Page.Header :crumbs="props.breadcrumbs">
        <EditorModeDropdown
          v-model="mode"
          v-model:template="template"
          :subject="title"
        />
        <Page.HeaderAction
          v-if="!props.playground"
          label="Preview"
          hotkey="$mod+p"
          variant="secondary"
          :active="showingPreview"
          @click="showingPreviewState = !showingPreviewState"
        />
        <Page.HeaderAction
          v-if="props.docsUrl"
          label="Docs"
          :href="props.docsUrl"
          variant="secondary"
        />
        <slot name="actions" />
      </Page.Header>

      <div class="empty:hidden">
        <FancyParseWarningCallout
          v-if="showFancyParseWarning"
          @close="showFancyParseWarning = false"
          @switch="mode = 'plaintext'"
        />
        <div
          v-if="props.warning"
          class="flex flex-col gap-2 border-y border-y-yellow-300 bg-yellow-100 px-4 py-3 text-yellow-600 @container first:border-t-0 @lg:flex-row @lg:items-center @lg:py-2"
        >
          <span class="grow text-sm">{{ props.warning }}</span>
        </div>
      </div>

      <div
        ref="scrollRef"
        class="relative flex h-full min-h-0 flex-1 basis-0 flex-col overflow-y-auto pt-2"
      >
        <TitleInput
          v-if="title !== undefined"
          v-model="title"
          class="text-2xl"
          style="padding-inline: max(20px, 50% - 650px / 2)"
          @focus-editor="activeEditor?.focus()"
        />

        <!-- Suspense is keyed so we'll show the fallback on mode changes -->
        <Suspense :key="mode">
          <TiptapEditor
            v-if="mode === 'fancy'"
            ref="activeEditor"
            v-model:body="body"
            :extensions="extensions"
            :template-tags="templateTags"
            @parse-error="showFancyParseWarning = true"
          />
          <PlainEditor
            v-else-if="mode === 'plaintext'"
            ref="activeEditor"
            v-model:body="body"
          />
          <NakedEditor
            v-else-if="mode === 'naked'"
            ref="activeEditor"
            v-model:body="body"
          />

          <template #fallback>
            <EditorSkeleton />
          </template>
        </Suspense>

        <Tray.Save
          :dirty="hasUnsavedChanges"
          @save="saveChanges"
          @reset="resetChanges"
        />
      </div>

      <RouterView />
    </template>

    <template #panel-2>
      <PreviewPanel
        :pending="preview.pending"
        :error="preview.error"
        :result="preview.body"
        :contexts="previewContexts"
        :draft-to="props.draftTo"
        :playground="props.playground"
        :show-subscriber-selector="props.showSubscriberSelector"
        :selected-subscriber-id="selectedSubscriberId"
        @update:showing-preview="showingPreview = $event"
        @update:selected-subscriber-id="selectedSubscriberId = $event"
      />
    </template>
  </ResizablePanels>
</template>

<script lang="ts" setup>
import {
  computed,
  defineAsyncComponent,
  ref,
  useTemplateRef,
  watch,
} from "vue";

import ResizablePanels, {
  useShowingPreview,
} from "@/components/layout/ResizablePanels.vue";
import Page from "@/design_system/Page";
import type { BreadcrumbPath } from "@/design_system/Page/Header.vue";
import Tray from "@/design_system/Tray";
import CodeBracketIcon from "@/icons/heroicons/code-bracket-micro.svg";
import SparklesIcon from "@/icons/heroicons/sparkles-micro.svg";
import {
  type DraftPreviewOptions,
  type TransactionalEmail,
  useDraftPreviewQuery,
} from "@/lib/api/queries/emails/render";
import { useSubscribePagePreviewQuery } from "@/lib/api/queries/misc/subscription";
import { debouncedRef } from "@/lib/composables/debounced-ref";
import { clone } from "@/lib/dirty";
import { looksLikeHtml } from "@/lib/html-detection";
import { OkapiError } from "@/lib/openapi";
import { showToast } from "@/lib/toasts";
import type { RouteLocation } from "@/router/types";
import TitleInput from "@/screens/email-detail/editor/components/TitleInput.vue";
import EditorSkeleton from "@/screens/email-detail/editor/EditorSkeleton.vue";
import FancyParseWarningCallout from "@/screens/email-detail/editor/FancyParseWarningCallout.vue";
import EditorModeDropdown from "@/screens/email-detail/EditorModeDropdown.vue";
import PreviewPanel from "@/screens/email-detail/PreviewPanel.vue";
import { useFeaturePermitted } from "@/store/stores/account";
import {
  type PreviewContext,
  useContextualSearchRoutes,
  useStore as useUIStatesStore,
} from "@/store/stores/ui-states";
import type { EditorMode, NewsletterEmailTemplate } from "@/types/email";
import { isEditorMode } from "@/types/email";
import type { Newsletter } from "@/types/newsletter";

const PlainEditor = defineAsyncComponent({
  loader: () => import("@/screens/email-detail/editor/PlainEditor.vue"),
});
const TiptapEditor = defineAsyncComponent({
  loader: () => import("@/screens/email-detail/editor/TiptapEditor.vue"),
});
const NakedEditor = defineAsyncComponent({
  loader: () => import("@/screens/email-detail/editor/NakedEditor.vue"),
});

const activeEditor = useTemplateRef("activeEditor");
const uiStates = useUIStatesStore();

const canUseNakedMode = useFeaturePermitted("naked_mode");

export type PreviewConfig =
  | { type: "email_draft" }
  | { type: "transactional_draft"; kind: TransactionalEmail }
  | { type: "feed_draft"; id: string }
  | { type: "newsletter_draft"; context: PreviewContext }
  | { type: "subscribe_page" };

const props = defineProps<{
  breadcrumbs: BreadcrumbPath;
  previewConfig: PreviewConfig;
  save: () => Promise<void>;
  newsletter: Newsletter;
  draftTo?: RouteLocation;
  docsUrl?: string;
  extensions?: string[];
  templateTags?: string[];
  warning?: string;
  playground?: boolean;
  showSubscriberSelector?: boolean;
}>();

const selectedSubscriberId = ref<string | undefined>(undefined);

useContextualSearchRoutes({
  "/__special_actions/fancy-mode": {
    title: "Switch to fancy mode",
    icon: SparklesIcon,
    action: () => {
      mode.value = "fancy";
    },
  },
  "/__special_actions/markdown-mode": {
    title: "Switch to markdown mode",
    icon: CodeBracketIcon,
    action: () => {
      mode.value = "plaintext";
    },
  },
});

const previewContexts = computed((): PreviewContext[] => {
  const config = props.previewConfig;
  switch (config.type) {
    case "email_draft":
    case "transactional_draft":
    case "feed_draft":
      return ["email"];
    case "subscribe_page":
      return ["subscribe"];
    case "newsletter_draft":
      return [config.context];
    default: {
      const _exhaustiveCheck: never = config;
      return _exhaustiveCheck;
    }
  }
});

const body = defineModel<string>({ required: true });
const title = defineModel<string>("title");
const template = defineModel<NewsletterEmailTemplate | null | undefined>(
  "template"
);

const initialBody = ref(body.value);
const initialTitle = ref(title.value);

const mode = ref<EditorMode>(
  (new URLSearchParams(window.location.search).get(
    "editor_mode"
  ) as EditorMode) || "fancy"
);

const showingPreviewState = useShowingPreview();
const showingPreview = computed(
  () => props.playground || showingPreviewState.value
);

const showFancyParseWarning = ref(false);

const saveChanges = async () => {
  try {
    await props.save();

    initialTitle.value = title.value;
    initialBody.value = body.value;
  } catch (err) {
    showToast({
      type: "error",
      title: "Failed to save changes",
      message:
        err instanceof OkapiError ? err.message : "Please try again later.",
    });
  }
};

const hasUnsavedChanges = computed(() => {
  if (props.playground) {
    return false;
  }
  return title.value !== initialTitle.value || body.value !== initialBody.value;
});

const resetChanges = () => {
  body.value = initialBody.value;
  title.value = initialTitle.value;
};

{
  const urlMode = new URLSearchParams(window.location.search).get(
    "editor_mode"
  ) as EditorMode | null;

  if (isEditorMode(urlMode)) {
    mode.value = urlMode;
  } else {
    if (template.value === "naked") {
      mode.value = "naked";
    } else if (
      canUseNakedMode.value &&
      body.value &&
      looksLikeHtml(body.value)
    ) {
      // If content looks like HTML and user has access to naked mode, default to naked mode
      mode.value = "naked";
      // Also set template to "naked" so it persists correctly
      if (!template.value) {
        template.value = "naked";
      }
    } else {
      mode.value = uiStates.editorMode;
    }
  }

  watch(mode, (next) => {
    if (next !== "naked") {
      uiStates.editorMode = next;
    }
  });
}

const debouncedPreviewOptions = debouncedRef(
  computed((): DraftPreviewOptions => {
    const config = props.previewConfig;

    switch (config.type) {
      case "email_draft": {
        return {
          type: "email_draft",
          email: {
            id: "preview",
            subject: title.value || "",
            body: body.value,
            template: template.value ?? undefined,
          },
          previews: ["email"],
        };
      }

      case "transactional_draft": {
        return {
          type: "transactional_draft",
          email: {
            type: config.kind,
            subject: title.value || "",
            body: body.value,
            template: template.value ?? undefined,
          },
          previews: ["email"],
        };
      }

      case "feed_draft": {
        return {
          type: "feed_draft",
          id: config.id,
          email: {
            subject: title.value || "",
            body: body.value,
          },
          previews: ["email"],
        };
      }

      case "newsletter_draft": {
        const draftNewsletter = clone(props.newsletter);

        return {
          type: "newsletter_draft",
          newsletter: draftNewsletter,
          subscriberId: selectedSubscriberId.value,
          previews: [config.context],
        };
      }
    }

    return {
      type: "email_draft",
      email: {
        id: "",
        subject: "",
        body: "",
      },
      previews: ["email"],
    };
  }),
  500,
  { enabled: showingPreview }
);

const emailPreview = useDraftPreviewQuery({
  enabled: computed(() => {
    return (
      showingPreview.value && props.previewConfig.type !== "subscribe_page"
    );
  }),
  options: computed(() => debouncedPreviewOptions.value),
});

const subscribePreview = useSubscribePagePreviewQuery({
  enabled: computed(() => {
    return (
      showingPreview.value && props.previewConfig.type === "subscribe_page"
    );
  }),
  newsletter: debouncedRef(
    computed(() => clone(props.newsletter)),
    500,
    { enabled: showingPreview }
  ),
});

const preview = computed(() => {
  if (props.previewConfig.type === "subscribe_page") {
    return {
      pending: subscribePreview.isFetching.value,
      error: subscribePreview.error.value,
      body: subscribePreview.data.value,
    };
  }

  return {
    pending: emailPreview.isFetching.value,
    error: emailPreview.error.value,
    body: emailPreview.data.value,
  };
});
</script>
